import type { PriceDistributionBucket, SessionPricePoint } from '@/types/session';

const MIN_DISTRIBUTION_ROWS = 11;
const SESSION_OPEN_MINUTES = 9 * 60 + 30;
const SESSION_CLOSE_MINUTES = 16 * 60;
const NICE_STEPS = [0.1, 0.25, 0.5, 1, 2, 2.5, 5, 10, 20, 25, 50, 100, 250, 500];

/** Axis ticks for the regular session, positioned by minutes after the 09:30 open. */
// Built once: the path chart converts every bar on each live re-render.
const MARKET_CLOCK_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export const SESSION_TIME_TICKS = [
  { label: '09:30', minutes: 0 },
  { label: '11:00', minutes: 90 },
  { label: '12:30', minutes: 180 },
  { label: '14:00', minutes: 270 },
  { label: '16:00', minutes: 390 },
] as const;

/**
 * Keeps the strikes that saw trading plus the current-price strike, padded by one
 * strike on each side and widened to at least eleven rows. Highest strike first.
 */
export function selectDistributionWindow(
  distribution: readonly PriceDistributionBucket[],
  currentPrice: number | null,
): PriceDistributionBucket[] {
  const rows = [...distribution].sort((a, b) => b.strike - a.strike);
  if (rows.length === 0) {
    return rows;
  }

  let currentIndex = rows.findIndex((row) => row.current);
  if (currentIndex === -1 && currentPrice != null) {
    currentIndex = rows.reduce(
      (best, row, index) =>
        Math.abs(row.strike - currentPrice) < Math.abs(rows[best].strike - currentPrice)
          ? index
          : best,
      0,
    );
  }

  const anchors = rows.flatMap((row, index) =>
    row.percent > 0 || index === currentIndex ? [index] : [],
  );
  let lo = Math.max(0, (anchors[0] ?? 0) - 1);
  let hi = Math.min(rows.length - 1, (anchors.at(-1) ?? 0) + 1);
  while (hi - lo + 1 < MIN_DISTRIBUTION_ROWS && (lo > 0 || hi < rows.length - 1)) {
    if (lo > 0) lo--;
    if (hi - lo + 1 < MIN_DISTRIBUTION_ROWS && hi < rows.length - 1) hi++;
  }

  return rows.slice(lo, hi + 1);
}

/** Y of a price on a column of strike rows (highest first), interpolated between row centers. */
export function priceToRowY(
  strikes: readonly number[],
  price: number,
  top: number,
  rowHeight: number,
): number | null {
  for (let i = 0; i < strikes.length - 1; i++) {
    const upper = strikes[i];
    const lower = strikes[i + 1];
    if (price <= upper && price >= lower) {
      const fraction = upper === lower ? 0 : (upper - price) / (upper - lower);
      return top + (i + fraction) * rowHeight + rowHeight / 2;
    }
  }
  return strikes.length === 1 && strikes[0] === price ? top + rowHeight / 2 : null;
}

/** Picks a round gridline step that gives roughly four to seven lines across the range. */
export function niceStep(range: number): number {
  const target = range / 5;
  return NICE_STEPS.find((step) => step >= target) ?? NICE_STEPS[NICE_STEPS.length - 1];
}

/** Minutes after 09:30 ET for an ISO timestamp, or null when unparseable. */
export function sessionMinutes(timestamp: string): number | null {
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  const parts = MARKET_CLOCK_FORMATTER.formatToParts(parsed);
  const hour = Number(parts.find((part) => part.type === 'hour')?.value);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value);
  return hour * 60 + minute - SESSION_OPEN_MINUTES;
}

export const SESSION_LENGTH_MINUTES = SESSION_CLOSE_MINUTES - SESSION_OPEN_MINUTES;

export function pricePathExtent(path: readonly SessionPricePoint[], currentPrice: number | null) {
  const prices = [...path.map((point) => point.price), currentPrice ?? Number.NaN].filter(
    Number.isFinite,
  );
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const step = niceStep(max - min || Math.max(min * 0.002, 0.1));
  return {
    step,
    lo: Math.floor(min / step) * step,
    hi: Math.ceil(max / step) * step,
  };
}
